import type { Checkout } from '../../checkouts';
import type { Customer } from '../../customers';
import type { ColoredLabel as TicketLabel } from '../../groups';
import type { Item } from '../../items';
import type { Kit } from '../../kits';
import type { Location } from '../../locations';
import type { Reservation } from '../../reservations';
import { CUSTOM_FIELD_WILDCARD_STUB_PATHS, ResourceType } from '../../resources';

export type { ColoredLabel as TicketLabel } from '../../groups';

export enum TicketStatus {
	DRAFT = 'draft',
	OPEN = 'open',
	IN_PROGRESS = 'in_progress',
	CLOSED = 'closed',
	CANCELLED = 'cancelled',
}

export interface Ticket {
	_id: string;
	ticketTypeId: string;
	name: string;
	number: string | null;
	label: TicketLabel | string | null;
	priority?: string | null;
	description?: string;
	status: TicketStatus;
	reporter: TicketCustomer | null;
	assignee: TicketCustomer | null;
	items: Item[];
	kits: Kit[];
	reservations: Reservation[];
	orders: Checkout[];
	tickets: Ticket[];
	location: Partial<Location> | null;
	fields: Record<string, unknown>;
	created: string | null;
	modified: string | null;
	cancelled: string | null;
	started: string | null;
	closed: string | null;
	archived: string | null;
}

export interface TicketReference {
	_id: string;
	name: string;
	status: string;
	number: string;
	fields?: Record<string, unknown>;
}

// Query variables
export interface TicketIdVariables {
	ticketId: string;
}

export type TicketCustomer = Pick<Customer, '_id' | 'name' | 'status' | 'user' | 'fields'>;

export type TicketCustomFieldKey = `fields.${string}` | `customer.fields.${string}` | `customer.${string}`;
export type TicketSystemField =
	| 'number'
	| 'name'
	| 'reporter'
	| 'assignee'
	| 'priority'
	| 'description'
	| 'location'
	| 'cancelled'
	| 'closed'
	| 'started'
	| 'modified'
	| 'created'
	| 'archived'
	| 'label'
	| 'ticketTypeId'
	| 'items'
	| 'reservations'
	| 'orders'
	| 'tickets';
export type TicketField = TicketSystemField | TicketCustomFieldKey;

// Reference mutation types
export interface CanOperationResponse {
	_id: string;
	result: boolean;
	message: string;
}

export interface MutateReferencesVariables {
	ticketId: string;
	referenceType: ResourceType;
	references: string[];
}

// Constants and helpers
export const TICKET__FIELDS = [
	'*',
	'label',
	'priority',
	'ticketType._id',
	'ticketType.name',
	'location._id',
	'location.name',
	'location.locationPath._id',
	'location.locationPath.name',
	'reporter._id',
	'reporter.name',
	'reporter.status',
	'assignee._id',
	'assignee.name',
	'assignee.status',
	// Detail GET has no column/kind list — wildcard stubs expand customer-kind values.
	'fields.*',
	...CUSTOM_FIELD_WILDCARD_STUB_PATHS,
	'created',
	'modified',
	'started',
	'archived',
	'closed',
	'cancelled',
].join(',');

export const TICKET_SYSTEM_FIELDS = [
	{
		id: 'number',
		name: 'Number',
		kind: 'string',
		editor: 'text',
		unit: null,
	},
	{
		id: 'name',
		name: 'Name',
		kind: 'string',
		editor: 'text',
		unit: null,
	},
	{
		id: 'reporter',
		name: 'Reporter',
		kind: 'string',
		editor: 'text',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'assignee',
		name: 'Assignee',
		kind: 'string',
		editor: 'text',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'description',
		name: 'Description',
		kind: 'string',
		editor: 'text',
		unit: null,
	},
	{
		id: 'location',
		name: 'Location',
		kind: 'string',
		editor: 'text',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'cancelled',
		name: 'Cancelled',
		kind: 'datetime',
		editor: 'date',
		unit: null,
	},
	{
		id: 'closed',
		name: 'Closed',
		kind: 'datetime',
		editor: 'date',
		unit: null,
	},
	{
		id: 'started',
		name: 'Started',
		kind: 'datetime',
		editor: 'date',
		unit: null,
	},
	{
		id: 'modified',
		name: 'Modified',
		kind: 'datetime',
		editor: 'date',
		unit: null,
	},
	{
		id: 'created',
		name: 'Created',
		kind: 'datetime',
		editor: 'date',
		unit: null,
	},
	{
		id: 'archived',
		name: 'Archived',
		kind: 'datetime',
		editor: 'date',
		unit: null,
	},
	{
		id: 'label',
		name: 'Label',
		kind: 'string',
		editor: 'text',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'items',
		name: 'Items',
		kind: 'string',
		excludeFromFilters: true,
	},
	{
		id: 'reservations',
		name: 'Reservations',
		kind: 'string',
		excludeFromFilters: true,
	},
	{
		id: 'orders',
		name: 'Check-outs',
		kind: 'string',
		excludeFromFilters: true,
	},
	{
		id: 'tickets',
		name: 'Tickets',
		kind: 'string',
		excludeFromFilters: true,
	},
	{
		id: 'kits',
		name: 'Kits',
		kind: 'string',
		excludeFromFilters: true,
	},
] as const;

export const USER_SYSTEM_FIELDS = [
	{
		id: 'reporter.email',
		name: 'Reporter email',
		kind: 'string',
		editor: 'email',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'reporter.name',
		name: 'Reporter name',
		kind: 'string',
		editor: 'text',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'assignee.email',
		name: 'Assignee email',
		kind: 'string',
		editor: 'email',
		unit: null,
		excludeFromFilters: true,
	},
	{
		id: 'assignee.name',
		name: 'Assignee name',
		kind: 'string',
		editor: 'text',
		unit: null,
		excludeFromFilters: true,
	},
];

export const ticketFieldIdsByName = {
	reporter: 'reporter' as const,
	assignee: 'assignee' as const,
	description: 'description' as const,
	location: 'location' as const,
	reservations: 'reservations' as const,
	orders: 'orders' as const,
	tickets: 'tickets' as const,
	cancelled: 'cancelled' as const,
	closed: 'closed' as const,
	started: 'started' as const,
	modified: 'modified' as const,
	created: 'created' as const,
	archived: 'archived' as const,
	number: 'number' as const,
	label: 'label' as const,
};
